/**
 * Erfolge (Gamification, E13): Bestwerte, Wochenringe, Serien, Abzeichen.
 *
 * Alles wird aus Daten **berechnet**, die die App ohnehin hat — keine Punkte,
 * keine erfundenen Zahlen, nichts, das nur fürs Antippen belohnt. Gefeiert
 * wird Verhalten (trainiert, Protein erreicht, genommen), nie ein Körperwert:
 * Die App protokolliert, sie verordnet nicht.
 *
 * Reine Funktionen: App (Anzeige) und Scheduler (Sonntags-Mitteilung) rechnen
 * mit denselben.
 */

import { childLevelOf, childMayUse, moduleOn } from './modules.js';
import type { ChildAccess } from './modules.js';
import { addDays, isoDateInZone } from './time.js';
import type { IsoDate } from './time.js';

// --- Bestwerte ---------------------------------------------------------------

/**
 * Unterhalb dieser Verbesserung ist ein „Bestwert" Rundungsrauschen der
 * Schätzformel — gefeiert wird erst ab einem halben Kilo.
 */
export const BEST_MIN_GAIN_KG = 0.5;

/**
 * Ist `candidate` ein neuer Bestwert gegenüber `previousBest`?
 *
 * Ohne früheren Wert nie: Der erste Satz einer Übung ist ein Anfang, kein
 * Rekord — sonst feierte die App jede neue Übung.
 */
export function isNewBest(previousBest: number | null, candidate: number | null): boolean {
  if (previousBest === null || candidate === null) return false;
  return candidate >= previousBest + BEST_MIN_GAIN_KG;
}

/**
 * Die Tage, an denen ein neuer Bestwert stand — aus dem Verlauf je Trainingstag
 * (älteste zuerst, wie `bestPerDay`). Der erste Tag zählt nicht (siehe oben).
 */
export function recordDays(
  history: readonly { day: string; kg: number }[],
): { day: string; kg: number }[] {
  const result: { day: string; kg: number }[] = [];
  let best: number | null = null;
  for (const point of history) {
    if (isNewBest(best, point.kg)) result.push(point);
    if (best === null || point.kg > best) best = point.kg;
  }
  return result;
}

// --- Wochenringe ---------------------------------------------------------------

/** An wie vielen Tagen der Woche das Proteinziel reicht, um den Ring zu schließen. */
export const PROTEIN_RING_DAYS = 5;

export type RingKey = 'training' | 'protein' | 'supplements';

export interface Ring {
  key: RingKey;
  done: number;
  /** `null` = kein Ziel für diesen Ring (keine Trainingstage, kein Proteinziel, nichts fällig). */
  goal: number | null;
  closed: boolean;
}

export interface RingInput {
  /** Heute, Ortszeit. Die Woche läuft von Montag bis Sonntag. */
  today: string;
  timeZone: string;
  now: Date;
  sessions: readonly { startedAt: string; deletedAt?: string | null }[];
  /** Trainingstermine der Woche, die nicht abgesagt sind (`trainingOccurrences`). */
  plannedTrainings: number;
  food: readonly { date: string; protein: number; deletedAt?: string | null }[];
  proteinTarget: number | null;
  intakes: readonly {
    dueAt: string;
    acknowledgedAt: string | null;
    deletedAt?: string | null;
  }[];
}

/** Montag der Woche eines Tages (ISO-Woche). */
export function mondayOf(date: string): string {
  const weekday = new Date(`${date}T12:00:00Z`).getUTCDay() || 7;
  return addDays(date as IsoDate, 1 - weekday);
}

/**
 * Drei Ringe für die laufende Kalenderwoche.
 *
 * - **Training:** Einheiten gegen die geplanten Trainingstage. Abgesagte zählen
 *   nicht als Ziel — absagen bricht nichts.
 * - **Protein:** Tage mit erreichtem Ziel, geschlossen ab fünf von sieben —
 *   eine Woche ist gnädiger als jeder einzelne Tag.
 * - **Supplements:** genommen gegen bisher fällig. Was heute Abend fällig wird,
 *   ist noch kein Versäumnis.
 */
export function weekRings(input: RingInput): { from: string; to: string; rings: Ring[] } {
  const from = mondayOf(input.today);
  const to = addDays(from as IsoDate, 6);
  const inWeek = (date: string) => date >= from && date <= to;

  const sessions = input.sessions.filter(
    (session) =>
      !session.deletedAt && inWeek(isoDateInZone(new Date(session.startedAt), input.timeZone)),
  ).length;
  const trainingGoal = input.plannedTrainings > 0 ? input.plannedTrainings : null;

  const proteinPerDay = new Map<string, number>();
  for (const entry of input.food) {
    // Geplantes steht schon im Protokoll (18.10.2026) — ein künftiger Tag
    // hat das Ziel noch nicht erreicht.
    if (entry.deletedAt || !inWeek(entry.date) || entry.date > input.today) continue;
    proteinPerDay.set(entry.date, (proteinPerDay.get(entry.date) ?? 0) + entry.protein);
  }
  const proteinDays =
    input.proteinTarget === null
      ? 0
      : [...proteinPerDay.values()].filter((value) => value >= input.proteinTarget!).length;

  let due = 0;
  let taken = 0;
  for (const intake of input.intakes) {
    if (intake.deletedAt) continue;
    const at = new Date(intake.dueAt);
    if (at > input.now || !inWeek(isoDateInZone(at, input.timeZone))) continue;
    due += 1;
    if (intake.acknowledgedAt) taken += 1;
  }

  const ring = (key: RingKey, done: number, goal: number | null): Ring => ({
    key,
    done,
    goal,
    closed: goal !== null && done >= goal,
  });
  return {
    from,
    to,
    rings: [
      ring('training', sessions, trainingGoal),
      ring('protein', proteinDays, input.proteinTarget === null ? null : PROTEIN_RING_DAYS),
      ring('supplements', taken, due > 0 ? due : null),
    ],
  };
}

/** „2 von 3 Ringen geschlossen" — nur Ringe mit Ziel zählen. */
export function ringSummary(rings: readonly Ring[]): { closed: number; total: number } {
  const counted = rings.filter((ring) => ring.goal !== null);
  return { closed: counted.filter((ring) => ring.closed).length, total: counted.length };
}

// --- Serien mit Joker -----------------------------------------------------------

/** Ein Joker je Kalendermonat rettet eine Tagesserie — Krankheit, Urlaub, Leben. */
export const JOKERS_PER_MONTH = 1;

export type PeriodState = 'ok' | 'miss' | 'neutral';

export interface Streak {
  current: number;
  best: number;
  /** Joker, die die laufende Serie schon verbraucht hat. */
  jokersUsed: number;
}

/**
 * Serie über aufeinanderfolgende Zeiträume (Tage oder Wochen), älteste zuerst.
 *
 * - `neutral` (nichts fällig, keine Trainingstage) zählt nicht und bricht nicht.
 * - Der **laufende** Zeitraum (`current`) bricht nie: Heute ist noch nicht vorbei.
 * - Mit `jokersPerMonth` rettet ein Joker je Kalendermonat einen Fehltag
 *   (Schlüssel `YYYY-MM-DD`; der Monat sind die ersten sieben Zeichen).
 */
export function streak(
  periods: readonly { key: string; state: PeriodState }[],
  current: string,
  jokersPerMonth = 0,
): Streak {
  const run = (list: readonly { key: string; state: PeriodState }[]) => {
    let count = 0;
    let best = 0;
    let jokersUsed = 0;
    const used = new Map<string, number>();
    for (const period of list) {
      if (period.state === 'neutral') continue;
      if (period.state === 'ok') {
        count += 1;
        best = Math.max(best, count);
        continue;
      }
      if (period.key === current) continue;
      const month = period.key.slice(0, 7);
      if ((used.get(month) ?? 0) < jokersPerMonth) {
        used.set(month, (used.get(month) ?? 0) + 1);
        jokersUsed += 1;
        continue;
      }
      count = 0;
      jokersUsed = 0;
      used.clear();
    }
    return { count, best, jokersUsed };
  };
  const result = run(periods);
  return { current: result.count, best: result.best, jokersUsed: result.jokersUsed };
}

/**
 * Supplement-Tage: `ok`, wenn alles bis jetzt Fällige genommen ist; `miss`,
 * wenn etwas offen blieb oder übersprungen wurde; `neutral` ohne Fälliges.
 */
export function supplementDays(
  intakes: readonly {
    dueAt: string;
    acknowledgedAt: string | null;
    deletedAt?: string | null;
  }[],
  timeZone: string,
  now: Date,
): { key: string; state: PeriodState }[] {
  const perDay = new Map<string, { due: number; taken: number }>();
  for (const intake of intakes) {
    if (intake.deletedAt) continue;
    const at = new Date(intake.dueAt);
    if (at > now) continue;
    const day = isoDateInZone(at, timeZone);
    const entry = perDay.get(day) ?? { due: 0, taken: 0 };
    entry.due += 1;
    if (intake.acknowledgedAt) entry.taken += 1;
    perDay.set(day, entry);
  }
  return [...perDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => ({ key, state: value.taken >= value.due ? 'ok' : 'miss' }));
}

/** Tage von `from` bis `to` mit `ok`, wenn der Tag in `done` steht — sonst `miss`. */
export function dailyPeriods(
  done: ReadonlySet<string>,
  from: string,
  to: string,
): { key: string; state: PeriodState }[] {
  const result: { key: string; state: PeriodState }[] = [];
  for (let day = from; day <= to; day = addDays(day as IsoDate, 1)) {
    result.push({ key: day, state: done.has(day) ? 'ok' : 'miss' });
  }
  return result;
}

// --- Abzeichen --------------------------------------------------------------------

export interface BadgeStats {
  /** Tage der Einheiten, älteste zuerst (Ortszeit). */
  sessionDays: readonly string[];
  /** Größtes Volumen einer einzelnen Einheit in kg. */
  bestSessionVolumeKg: number;
  /** Rekordtage über alle Übungen, älteste zuerst. */
  recordDays: readonly string[];
  bestSupplementStreak: number;
  bestTrainingWeekStreak: number;
  /** Verschiedene Gerichte, die gegessen wurden. */
  distinctRecipesEaten: number;
  journalDays: number;
  /** Tage mit selbst Eingetragenem im Ernährungsprotokoll, älteste zuerst (`foodLogDays`). */
  foodDays: readonly string[];
  /** Tage mit erreichtem Wasserziel, älteste zuerst (`waterGoalDays`). */
  waterDays: readonly string[];
}

export type BadgeKey =
  | 'sessions'
  | 'volume'
  | 'records'
  | 'supplementStreak'
  | 'trainingWeeks'
  | 'recipes'
  | 'journal'
  | 'foodDays'
  | 'waterDays';

/** Stufen je Abzeichen — jede Stufe ist ein eigenes Abzeichen. */
export const BADGE_LEVELS: Record<BadgeKey, readonly number[]> = {
  sessions: [1, 10, 50, 100, 250],
  volume: [5000, 10000],
  records: [1, 10, 50],
  supplementStreak: [7, 30, 100],
  trainingWeeks: [4, 12],
  recipes: [10, 25],
  journal: [30, 100],
  foodDays: [7, 30, 100],
  waterDays: [7, 30, 100],
};

export interface Badge {
  key: BadgeKey;
  level: number;
  earned: boolean;
  /** Tag, an dem es erreicht wurde — nur, wo die Daten ihn hergeben. */
  earnedOn: string | null;
  progress: number;
}

export function badges(stats: BadgeStats): Badge[] {
  const value: Record<BadgeKey, number> = {
    sessions: stats.sessionDays.length,
    volume: stats.bestSessionVolumeKg,
    records: stats.recordDays.length,
    supplementStreak: stats.bestSupplementStreak,
    trainingWeeks: stats.bestTrainingWeekStreak,
    recipes: stats.distinctRecipesEaten,
    journal: stats.journalDays,
    foodDays: stats.foodDays.length,
    waterDays: stats.waterDays.length,
  };
  const dayOf: Partial<Record<BadgeKey, readonly string[]>> = {
    sessions: stats.sessionDays,
    records: stats.recordDays,
    foodDays: stats.foodDays,
    waterDays: stats.waterDays,
  };
  const result: Badge[] = [];
  for (const key of Object.keys(BADGE_LEVELS) as BadgeKey[]) {
    for (const level of BADGE_LEVELS[key]) {
      const earned = value[key] >= level;
      result.push({
        key,
        level,
        earned,
        earnedOn: earned ? (dayOf[key]?.[level - 1] ?? null) : null,
        progress: Math.min(value[key], level),
      });
    }
  }
  return result;
}

/**
 * Tage, an denen jemand selbst etwas ins Ernährungsprotokoll eingetragen hat
 * (bis heute, älteste zuerst). Geplantes steht seit 18.10.2026 ebenfalls im
 * Protokoll (`plannedLogId`) — das zählt hier nicht: Ein Wochenplan ist
 * kein Eintragen, sonst feierte die App das Vorausplanen als Regelmäßigkeit.
 */
export function foodLogDays(
  entries: readonly { id: string; date: string; deletedAt?: string | null }[],
  plannedIds: ReadonlySet<string>,
  today: string,
): string[] {
  const days = new Set<string>();
  for (const entry of entries) {
    if (entry.deletedAt || entry.date > today || plannedIds.has(entry.id)) continue;
    days.add(entry.date);
  }
  return [...days].sort();
}

/**
 * Tage mit erreichtem Wasserziel (bis heute, älteste zuerst). Gerechnet gegen
 * das **heutige** Ziel — ein früheres steht nirgends; wer es anhebt, verliert
 * so höchstens alte Tage, gewinnt aber nie welche dazu.
 */
export function waterGoalDays(
  entries: readonly { date: string; ml: number; deletedAt?: string | null }[],
  targetMl: number,
  today: string,
): string[] {
  const perDay = new Map<string, number>();
  for (const entry of entries) {
    if (entry.deletedAt || entry.date > today) continue;
    perDay.set(entry.date, (perDay.get(entry.date) ?? 0) + entry.ml);
  }
  return [...perDay.entries()]
    .filter(([, ml]) => ml >= targetMl)
    .map(([day]) => day)
    .sort();
}

// --- Wer welche Erfolge bekommt ---------------------------------------------------

export type StreakKey = 'supplements' | 'journal' | 'trainingWeeks';

/** Was ein Konto an Erfolgen sieht — Ringe, Serien, Abzeichen, Bestwerte am Satz. */
export interface AchievementScope {
  rings: readonly RingKey[];
  streaks: readonly StreakKey[];
  badges: readonly BadgeKey[];
  /** Bestwert am Satz und Rekordtage auf der Übungsseite. */
  records: boolean;
}

interface ScopePart {
  rings: readonly RingKey[];
  streaks: readonly StreakKey[];
  badges: readonly BadgeKey[];
}

/** Erfolge des Moduls Training — alles Verhalten oder Hantelgewicht. */
const TRAINING_PART: ScopePart = {
  rings: ['training'],
  streaks: ['trainingWeeks'],
  badges: ['sessions', 'volume', 'records', 'trainingWeeks'],
};

/** Erfolge des Moduls Ernährung (Supplements gehören dazu, wie bei den Ringen). */
const NUTRITION_PART: ScopePart = {
  rings: ['protein', 'supplements'],
  streaks: ['supplements'],
  badges: ['supplementStreak', 'recipes', 'foodDays', 'waterDays'],
};

const JOURNAL_PART: ScopePart = { rings: [], streaks: ['journal'], badges: ['journal'] };

/**
 * Was ein Teen nie bekommt, auch mit Freigabe (28.10.2026): Supplements (gibt
 * es für Kinder gar nicht) und den Proteinring — ein Ziel an fünf von sieben
 * Tagen zu „schaffen“ ist Diätdruck, und für Jugendliche hat das Fachkonzept
 * keine Quelle für ein Eiweißziel. Eintragen und Trinken zählen trotzdem.
 * Körperwerte stehen in keinem Erfolg — weder für Erwachsene noch für Kinder.
 */
export const TEEN_NEVER: ScopePart = {
  rings: ['protein', 'supplements'],
  streaks: ['supplements'],
  badges: ['supplementStreak'],
};

export const NO_ACHIEVEMENTS: AchievementScope = {
  rings: [],
  streaks: [],
  badges: [],
  records: false,
};

/**
 * Bekommt dieses Konto überhaupt Erfolge? Erwachsene (`child` = `null`)
 * immer, Kinder nur ab der Stufe `teen` (Timo, 28.10.2026: „Ja, sie sollen
 * auch Erfolge sehen.“). Abschalten kann man sie trotzdem
 * (`profiles.achievements`) — die Regel für App, Server und Scheduler.
 */
export function mayHaveAchievements(child: ChildAccess | null | undefined): boolean {
  return !child || childLevelOf(child.level) === 'teen';
}

/**
 * Welche Erfolge ein Konto sieht — die eine Stelle für App und Scheduler.
 *
 * - Erwachsene: alles, was zu einem eingeschalteten Modul gehört.
 * - Teen: Tagebuch immer; Training nur mit Freigabe, Ernährung nur mit
 *   Freigabe (`childMayUse`) — ohne `TEEN_NEVER`. Keine Ranglisten und kein
 *   Vergleich im Haushalt (die stehen in keinem Bereich hier).
 * - Kinder unter `teen`: nichts.
 *
 * `modules` sind die geltenden Module (für Kinder schon beschnitten); `null`
 * heißt wie überall „alles an“.
 */
export function achievementScope(
  child: ChildAccess | null | undefined,
  modules: readonly string[] | null | undefined,
): AchievementScope {
  if (!mayHaveAchievements(child)) return NO_ACHIEVEMENTS;
  const parts: ScopePart[] = [];
  const on = (module: 'training' | 'nutrition' | 'journal') =>
    moduleOn(modules, module) && (!child || childMayUse(child, module));
  if (on('training')) parts.push(TRAINING_PART);
  if (on('nutrition')) parts.push(NUTRITION_PART);
  if (on('journal')) parts.push(JOURNAL_PART);
  const never: ScopePart = child ? TEEN_NEVER : { rings: [], streaks: [], badges: [] };
  const pick = <K extends keyof ScopePart>(key: K) =>
    parts
      .flatMap((part) => part[key] as readonly string[])
      .filter((value) => !(never[key] as readonly string[]).includes(value));
  const badges = pick('badges') as BadgeKey[];
  return {
    rings: pick('rings') as RingKey[],
    streaks: pick('streaks') as StreakKey[],
    badges,
    records: badges.includes('records'),
  };
}

/** Gibt es in diesem Bereich überhaupt etwas zu zeigen? */
export function hasAchievements(scope: AchievementScope): boolean {
  return scope.rings.length + scope.streaks.length + scope.badges.length > 0;
}
